'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { ThumbsUp } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

import NextImage from '@/components/NextImage'
import { Button } from '@/components/ui/button'
import { EmojiPicker } from '@/components/ui/emoji-picker'

import { EmojiData, MemeReaction } from '@/types/meme.interface'
import { UserAuth } from '@/types/user.type'

interface MemePostReactionsProps {
  postId: string
  reactions: MemeReaction[]
  onReact: (postId: string, reactionType: string, emojiData: EmojiData) => Promise<void>
  currentUser?: UserAuth
  isAuthenticated?: boolean
  isReacting?: boolean
  modal?: boolean
  className?: string
  onRemoveCurrentUserReaction?: (postId: string) => Promise<void>
}

interface MemeEmojiData {
  id: number
  name: string
  imageUrl: string
}

// Default 5 quick reactions - same as comment reactions
export const defaultReactions: Record<
  string,
  {
    activeColor: string
    hoverBgColor: string
    hoverShadow: string
    activeBgColor: string
    hoverColor: string
    emoji: string
    name: string
  }
> = {
  like: {
    activeColor: 'text-blue-600',
    hoverBgColor: '#eff6ff',
    hoverShadow: '0 0 12px rgba(37, 99, 235, 0.6)',
    activeBgColor: '#dbeafe',
    hoverColor: '#2563eb',
    emoji: '👍',
    name: 'Like',
  },
  love: {
    activeColor: 'text-pink-600',
    hoverBgColor: '#fdf2f8',
    hoverShadow: '0 0 12px rgba(219, 39, 119, 0.6)',
    activeBgColor: '#fce7f3',
    hoverColor: '#db2777',
    emoji: '❤️',
    name: 'Love',
  },
  haha: {
    activeColor: 'text-yellow-600',
    hoverBgColor: '#fefce8',
    hoverShadow: '0 0 12px rgba(217, 119, 6, 0.6)',
    hoverColor: '#ca8a04',
    activeBgColor: '#fef9c3',
    emoji: '😂',
    name: 'Haha',
  },
  wow: {
    activeColor: 'text-yellow-500',
    hoverBgColor: '#fffbeb',
    hoverShadow: '0 0 12px rgba(245, 158, 11, 0.6)',
    activeBgColor: '#fef3c7',
    hoverColor: '#eab308',
    emoji: '😮',
    name: 'Wow',
  },
  sad: {
    activeColor: 'text-purple-600',
    hoverBgColor: '#faf5ff',
    hoverShadow: '0 0 12px rgba(147, 51, 234, 0.6)',
    activeBgColor: '#f3e8ff',
    hoverColor: '#9333ea',
    emoji: '😢',
    name: 'Sad',
  },
  angry: {
    activeColor: 'text-red-600',
    hoverBgColor: '#fef2f2',
    hoverShadow: '0 0 12px rgba(220, 38, 38, 0.6)',
    activeBgColor: '#fee2e2',
    hoverColor: '#dc2626',
    emoji: '😡',
    name: 'Angry',
  },
}

const reactionName = {
  like: 'Thích',
  love: 'Yêu thích',
  haha: 'Haha',
  wow: 'Wow',
  sad: 'Buồn',
  angry: 'Giận dữ',
}

export const renderReactionDisplay = (
  group: {
    emojiData?: EmojiData
    reactionType: string
    identifier: string
    count: number
    users: Array<{ id: number; name: string }>
  },
  size: 'small' | 'medium' | 'large' = 'medium'
) => {
  const sizeClasses = {
    small: '!size-4 text-xs',
    medium: '!size-5 text-sm',
    large: '!size-6 text-xl',
  }

  if (group.emojiData?.imageUrl) {
    const sizeImage = {
      small: 16,
      medium: 24,
      large: 28,
    }
    return (
      <NextImage
        src={group.emojiData.imageUrl}
        alt={group.emojiData.name || 'custom reaction'}
        width={sizeImage[size]}
        height={sizeImage[size]}
        className="rounded object-cover"
        title={group.emojiData.name || 'custom reaction'}
        unoptimized
      />
    )
  }

  if (group.emojiData?.unicode) {
    return (
      <span
        className={`${sizeClasses[size]} flex items-center justify-center`}
        title={group.emojiData.name || group.emojiData.unicode}
      >
        {group.emojiData.unicode}
      </span>
    )
  }

  const defaultConfig = defaultReactions[group.reactionType]
  if (defaultConfig) {
    return (
      <span className={`${sizeClasses[size]} ${defaultConfig.activeColor}`} title={defaultConfig.name}>
        {defaultConfig.emoji}
      </span>
    )
  }

  return <span className={sizeClasses[size]}>{group.identifier}</span>
}

function MemePostReactions({
  postId,
  reactions,
  onReact,
  currentUser,
  isAuthenticated = false,
  isReacting = false,
  modal = false,
  className,
  onRemoveCurrentUserReaction,
}: MemePostReactionsProps) {
  const [showReactionBar, setShowReactionBar] = React.useState(false)
  const [animatingReaction, setAnimatingReaction] = React.useState<string | null>(null)

  const reactionBarRef = React.useRef<HTMLDivElement>(null)

  const groupedReactions = React.useMemo(() => {
    const groups = new Map<
      string,
      {
        identifier: string
        reactionType: string
        emojiData?: EmojiData
        count: number
        users: Array<{ id: number; name: string }>
        userReacted: boolean
        isDefault: boolean
      }
    >()

    reactions.forEach(reaction => {
      // Skip reactions with missing user data
      if (!reaction.user) return

      // Create unique identifier for grouping
      let identifier: string
      let isDefault = false

      if (defaultReactions[reaction.reactionType]) {
        // It's a default reaction
        identifier = reaction.reactionType
        isDefault = true
      } else if (reaction.emojiData?.unicode) {
        // It's a custom emoji
        identifier = reaction.emojiData.unicode
      } else if (reaction.emojiData?.imageUrl) {
        // It's a custom meme
        identifier = reaction.emojiData.imageUrl
      } else {
        // Fallback
        identifier = reaction.reactionType
      }

      const existing = groups.get(identifier)

      if (existing) {
        existing.count++
        existing.users.push({
          id: reaction.user.id,
          name: `${reaction.user.firstName} ${reaction.user.lastName}`,
        })
        if (reaction.user.id === currentUser?.id) {
          existing.userReacted = true
        }
      } else {
        groups.set(identifier, {
          identifier,
          reactionType: reaction.reactionType,
          emojiData: reaction.emojiData,
          count: 1,
          users: [
            {
              id: reaction.user.id,
              name: `${reaction.user.firstName} ${reaction.user.lastName}`,
            },
          ],
          userReacted: reaction.user.id === currentUser?.id,
          isDefault,
        })
      }
    })

    return Array.from(groups.values()).sort((a, b) => {
      if (a.userReacted && !b.userReacted) return -1
      if (!a.userReacted && b.userReacted) return 1
      if (a.isDefault && !b.isDefault) return -1
      if (!a.isDefault && b.isDefault) return 1
      return b.count - a.count
    })
  }, [reactions, currentUser?.id])

  const userActiveReaction = groupedReactions.find(group => group.userReacted)

  const handleDefaultReactionClick = async (reactionType: string) => {
    if (!isAuthenticated || isReacting) return

    const config = defaultReactions[reactionType]
    if (!config) return

    const emojiData: EmojiData = {
      unicode: config.emoji,
      shortcode: `:${reactionType}:`,
      name: config.name,
      isCustom: false,
    }

    setAnimatingReaction(reactionType)
    await onReact(postId, reactionType, emojiData)
    setShowReactionBar(false)

    setTimeout(() => {
      setAnimatingReaction(null)
    }, 800)
  }

  const handleEmojiSelect = async (emoji: string) => {
    if (!isAuthenticated || isReacting) return

    const emojiData: EmojiData = {
      unicode: emoji,
      shortcode: `:${emoji}:`,
      name: emoji,
      isCustom: true,
    }

    setAnimatingReaction(emoji)
    await onReact(postId, `${emoji}`, emojiData)
    setShowReactionBar(false)

    setTimeout(() => {
      setAnimatingReaction(null)
    }, 800)
  }

  const handleCustomEmojiSelect = async (memeData: MemeEmojiData) => {
    if (!isAuthenticated || isReacting) return

    const emojiData: EmojiData = {
      unicode: memeData.name,
      imageUrl: memeData.imageUrl,
      shortcode: `${memeData.name}`,
      name: memeData.name,
      isCustom: true,
    }

    setAnimatingReaction(memeData.imageUrl)
    await onReact(postId, `${memeData.name}`, emojiData)
    setShowReactionBar(false)

    setTimeout(() => {
      setAnimatingReaction(null)
    }, 800)
  }

  const getAnimationVariant = (identifier: string) => {
    if (animatingReaction !== identifier) return {}

    return {
      scale: [1, 1.4, 1],
      transition: { duration: 0.6 },
    }
  }

  return (
    <div className={cn('flex items-center justify-between w-full', className)}>
      {/* Reaction Button */}
      <div className="relative w-full" ref={reactionBarRef}>
        <AnimatePresence>
          {showReactionBar && (
            <motion.div
              className="absolute bottom-full mb-2 flex items-center bg-white shadow-xl rounded-full py-2 px-3 z-20 border border-gray-100"
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95, transition: { duration: 0.15 } }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              onMouseLeave={() => setShowReactionBar(false)}
            >
              {/* Default 5 reactions */}
              {Object.entries(defaultReactions).map(([type, config]) => {
                const userHasThisReaction = groupedReactions.some(
                  group => group.reactionType === type && group.userReacted
                )

                return (
                  <motion.button
                    key={type}
                    onClick={() => handleDefaultReactionClick(type)}
                    className="flex items-center justify-center w-10 h-10 rounded-full mx-1 transition-colors text-lg"
                    style={{
                      backgroundColor: userHasThisReaction ? config.activeBgColor : 'transparent',
                    }}
                    title={config.name}
                    whileHover={{
                      scale: 1.3,
                      backgroundColor: config.hoverBgColor,
                      boxShadow: config.hoverShadow,
                      transition: { duration: 0.2 },
                    }}
                    whileTap={{ scale: 0.9 }}
                    animate={getAnimationVariant(type)}
                  >
                    <span className="text-lg">{config.emoji}</span>
                  </motion.button>
                )
              })}

              <div className="w-px h-6 bg-gray-200 mx-2" />

              <div className="relative z-[99999]">
                <EmojiPicker
                  onEmojiSelect={handleEmojiSelect}
                  onCustomEmojiSelect={handleCustomEmojiSelect}
                  buttonClassName="w-10 h-10 rounded-full hover:bg-gray-100 transition-colors flex items-center justify-center"
                  className="w-72 !z-[99999]"
                  buttonIconClassName="!size-5"
                  modal={modal}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <Button
          disabled={!isAuthenticated}
          title={!isAuthenticated ? 'Sign in to react' : ''}
          variant="ghost"
          className={cn(
            'flex items-center gap-2 py-2 hover:text-blue-500 hover:bg-blue-50 transition-all duration-200 w-full',
            userActiveReaction && 'text-blue-600'
          )}
          onMouseEnter={() => setShowReactionBar(true)}
          onClick={() => {
            if (userActiveReaction) {
              onRemoveCurrentUserReaction?.(postId)
            } else {
              setShowReactionBar(prev => !prev)
            }
          }}
        >
          {userActiveReaction ? (
            <>
              <motion.span initial={false} animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.4 }}>
                {renderReactionDisplay(userActiveReaction, 'large')}
              </motion.span>
              <span className="text-sm font-medium hidden sm:block">
                {/* @ts-expect-error: reactionType is a string */}
                {reactionName[userActiveReaction.reactionType]}
              </span>
            </>
          ) : (
            <>
              <motion.span className="text-gray-500" whileHover={{ rotate: 10 }}>
                <ThumbsUp className="h-4 w-4 hover:text-blue-500" />
              </motion.span>
              <span className="text-sm hidden sm:block">Thích</span>
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

export default MemePostReactions
